/****************************************************
 * Module Name:  mediaBuilder.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Button } from "@mui/material";

import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteIcon from '@mui/icons-material/Delete';
import StoneJourneyProxy from "../../utility/proxy/stonejourney/stoneJourneyProxy";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import renderCellExpand from "../common/dataGridCellExpand";
import { RiImageAddFill, RiVideoUploadFill, RiVideoDownloadFill } from "react-icons/ri";
import UploadProcessMediaModal from "./uploadProcessMediaModal";
import { VscFilePdf } from "react-icons/vsc";
import AddKapanPacketModal from "./addKapanpacketModal";
import PageTitle from "../common/pageTitle";

const MediaBuilder = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [sourceData, setSourceData] = useState({});
    const [showAddMediaModal, setShowAddMediaModal] = useState(false);
    const [showAddKapanPacketModal, setShowAddKapanPacketModal] = useState(false);
    const [mediaType, setMediaType] = useState("");
    const [selectionModel, setSelectionModel] = useState([]);

    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.STONEJOURNEY_MEDIABUILDER);

    useEffect(() => {
        PageTitle("STONEJOURNEY_MEDIABUILDER");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (local_Permission.length > 0) {
            handleSearch()
        }
    }

    const handleSearch = () => {
        setLoader(true);
        StoneJourneyProxy.GetAllMediaBuilderData(
            (responseData) => {
                if (responseData) {
                    setData(responseData);
                    setLoader(false);
                } else {
                    setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                    setLoader(false);
                }
            }
        )
    };

    const handleKapanPacketSave = (data) => {
        setLoader(true);
        setShowAddKapanPacketModal(false);
        StoneJourneyProxy.AddKapanPacketData(data, (response) => {
            if (response) {
                setSnackbarState(true, "Data Added Successfully !!", "success");
                setLoader(false);
                setShowAddMediaModal(false);
                setSourceData({});
                handleSearch();
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
                setShowAddMediaModal(false);
                setSourceData({});
                handleSearch();
            }
        });

    }

    const deleteRecord = () => {
        setLoader(true);
        StoneJourneyProxy.DeleteStoneJourney(selectionModel, (response) => {
            if (response) {
                setSnackbarState(true, "Records Deleted Successfully !!", "success");
                setLoader(false);
                setSourceData({});
                handleSearch();
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
                setSourceData({});
                handleSearch();
            }
        });
    }

    const handleUpload = (data, kapanid, packetid, processid) => {
        setLoader(true);

        if (mediaType === "image") {
            StoneJourneyProxy.UploadProcessImages(data, kapanid, packetid, processid, (response) => {
                if (response.status === "Success") {
                    setSnackbarState(true, "Image Uploaded Successfully !!", "success");
                    setLoader(false);
                    setShowAddMediaModal(false);
                    setSourceData({});
                    handleSearch();
                } else {
                    setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                    setLoader(false);
                    setShowAddMediaModal(false);
                    setSourceData({});
                    handleSearch();
                }
            })
        } else {
            StoneJourneyProxy.UploadProcessVideos(data, kapanid, packetid, processid, (response) => {
                if (response.status === "Success") {
                    setSnackbarState(true, "Videos Uploaded Successfully !!", "success");
                    setLoader(false);
                    setShowAddMediaModal(false);
                    setSourceData({});
                    handleSearch();
                } else {
                    setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                    setLoader(false);
                    setShowAddMediaModal(false);
                    setSourceData({});
                    handleSearch();
                }
            })
        }
    }

    const downloadVideo = (kapanid, packetid) => {
        setLoader(true);
        StoneJourneyProxy.GetMergedVideo(kapanid, packetid, (response) => {
            if (response && response.size > 0) {

                const videoUrl = window.URL.createObjectURL(response);
                const a = document.createElement("a");
                a.href = videoUrl;
                a.download = "video_" + kapanid + "_" + packetid + ".mp4";
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(videoUrl);

                setSnackbarState(true, "Video downloaded Successfully !!", "success");
                setLoader(false);
            } else if (response) {
                setSnackbarState(true, "Video is not available, please create it !!", "warning");
                setLoader(false);
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
            }
        })
    }

    const downloadPdf = (kapanid, packetid) => {
        setLoader(true);
        StoneJourneyProxy.GetMergedPdf(kapanid, packetid, (response) => {
            if (response && response.size > 0) {
                const pdfUrl = window.URL.createObjectURL(response);
                const a = document.createElement("a");
                a.href = pdfUrl;
                a.download = "pdf_" + kapanid + "_" + packetid + ".pdf";
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(pdfUrl);

                setSnackbarState(true, "PDF downloaded Successfully !!", "success");
                setLoader(false);
            } else if (response) {
                setSnackbarState(true, "PDF is not available, please create it !!", "warning");
                setLoader(false);
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
            }
        })
    }

    const createVideo = (kapanid, packetid) => {
        setLoader(true);
        StoneJourneyProxy.CreateVideo(kapanid, packetid, (response) => {
            if (response.status === "Success") {
                setSnackbarState(true, "Please wait, Video will be ready after 5-10 mins", "info");
                setLoader(false);
            } else if (response.status === "warning") {
                setSnackbarState(true, response.errorMessage, "warning");
                setLoader(false);
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
            }
        })
    }

    const createPdf = (kapanid, packetid) => {
        setLoader(false);
        StoneJourneyProxy.createPdf(kapanid, packetid, (response) => {

            if (response.status === "Success") {
                setSnackbarState(true, "Please wait, PDF will be ready after 1-2 mins", "info");
                setLoader(false);
            } else if (response.status === "warning") {
                setSnackbarState(true, response.errorMessage, "warning");
                setLoader(false);
            } else {
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
            }
        })
    }

    const setSnackbarState = (show, message, variant) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
    };

    const initialColumns = () => [
        {
            field: "kapanid",
            headerName: "KAPAN ID",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: renderCellExpand,
        },
        {
            field: "packetid",
            headerName: "PACKET ID",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: renderCellExpand,
        },
        {
            field: "addimage",
            headerName: "ADD IMAGE",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        color='success'
                        onClick={() => {
                            setShowAddMediaModal(true)
                            setSourceData(params.row)
                            setMediaType("image");
                        }}
                        startIcon={<RiImageAddFill color="primary" size={20} />}
                    >
                        ADD IMAGE
                    </Button>
                );
            },
        },
        {
            field: "addvideo",
            headerName: "ADD VIDEO",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        color='success'
                        onClick={() => {
                            setShowAddMediaModal(true)
                            setSourceData(params.row)
                            setMediaType("video");
                        }}
                        startIcon={<RiVideoUploadFill color="primary" size={20} />}
                    >
                        ADD VIDEO
                    </Button>
                );
            },
        },
        {
            field: "createpdf",
            headerName: "CREATE PDF",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: '#454545', color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                        onClick={() => createPdf(params.row.kapanid, params.row.packetid)}
                        startIcon={<VscFilePdf color="primary" size={20} />}
                    >
                        CREATE PDF
                    </Button>
                );
            },
        },
        {
            field: "createvideo",
            headerName: "CREATE VIDEO",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: '#454545', color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                        onClick={() => createVideo(params.row.kapanid, params.row.packetid)}
                        startIcon={<RiVideoDownloadFill color="primary" size={20} />}
                    >
                        CREATE VIDEO
                    </Button>
                );
            },
        },
        {
            field: "downloadpdf",
            headerName: "DOWNLOAD PDF",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            downloadPdf(params.row.kapanid, params.row.packetid);
                        }}
                    >
                        {""}
                        <VscFilePdf color="primary" size={24} />
                    </Box>
                );
            },
        },
        {
            field: "downloadvideo",
            headerName: "DOWNLOAD VIDEO",
            headerAlign: "center",
            align: "center",
            width: 200,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            downloadVideo(params.row.kapanid, params.row.packetid);
                        }}
                    >
                        {""}
                        <RiVideoDownloadFill color="primary" size={24} />
                    </Box>
                );
            },
        }
    ];

    const columns = Utility.isUpdateAllowed(local_Permission)
        ? initialColumns()
        : initialColumns().filter((col) => col.field !== "edit");

    return (
        <Box>
            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" :
                    snackbarVariant === "error" ? "Failed" : snackbarVariant === "warning" ? "Warning" : snackbarVariant === "info" ? "Info" : ""}
                handleClose={() => setShowSnackbar(false)}
            />

            {showAddMediaModal ? (
                <UploadProcessMediaModal
                    show={showAddMediaModal}
                    loader={loader}
                    handleSave={handleUpload}
                    handleClose={() => setShowAddMediaModal(false)}
                    sourceData={sourceData}
                    mediaType={mediaType}
                />
            ) : null}

            {showAddKapanPacketModal ? (
                <AddKapanPacketModal
                    show={showAddKapanPacketModal}
                    handleSave={handleKapanPacketSave}
                    handleClose={() => setShowAddKapanPacketModal(false)}
                />
            ) : null}

            {local_Permission.length === 0 ? <NoPermission /> : (
                <Box px={1}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "83vh",
                            "& .lavenderCell": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                            },
                            marginTop: "1.5rem",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            getRowHeight={() => "auto"}
                            density="compact"
                            loading={loader}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            slots={{ toolbar: GridToolbar }}
                            rows={data}
                            columns={columns}
                            getRowId={(row) => `${row.kapanid}-${row.packetid}`}
                            checkboxSelection={true}
                            rowSelectionModel={selectionModel}
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setSelectionModel(newSelectionModel);
                            }}
                            rowSpacingType="border"
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                        <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                            {loader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        color="primary"
                                        fontSize="medium"
                                        onClick={() => handleSearch()}
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                            {Utility.isAddAllowed(local_Permission) ? (
                                <Tooltip title="Add Data">
                                    <Button
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                        sx={{ ml: "5px" }}
                                        onClick={() => setShowAddKapanPacketModal(true)
                                        }
                                        startIcon={<AddBoxOutlinedIcon size="large" />}
                                    >
                                        DATA
                                    </Button>
                                </Tooltip>
                            ) : null}
                            {Utility.isDeleteAllowed(local_Permission) ? (
                                <Tooltip title="Delete Data">
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                        sx={{ ml: "5px" }}
                                        onClick={() => deleteRecord()}
                                        startIcon={<DeleteIcon size="large" />}
                                    >
                                        Delete
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};


export default MediaBuilder;